import React, { useState } from 'react';
import axios from 'axios';
import { useModal } from '../context/ModalContext';
import ImageUpload from './ImageUpload';
import CustomSelect from './CustomSelect';
import '../styles/Modal.css';

const CreateBrandModal = ({ onClose, onSuccess, organizations, organization, brands, clients, apiPrefix }) => {
  const modal = useModal();

  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const presetOrganizationId = organization ? safeString(organization._id || organization.id) : '';

  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    organization_id: presetOrganizationId,
    owner_id: '',
    icon_url: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedOrgId = brandForm.organization_id;
    const brandsInOrg = brands.filter(b =>
      safeString(b.organization_id) === safeString(selectedOrgId)
    );

    if (brandsInOrg.length >= 3) {
      modal.error(
        'This organization has reached the maximum number of brands (3).\n\n' +
        'Please contact Velvet if additional brand slots are needed.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${apiPrefix}/create-brand`, brandForm, { withCredentials: true });
      modal.success('Brand created successfully!');
      setBrandForm({ brand_name: '', organization_id: presetOrganizationId, owner_id: '', icon_url: '' });
      if (onSuccess) await onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating brand:', error);
      modal.error(error.response?.data?.error || 'Failed to create brand');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Create Brand</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close" />
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="brand-name">Brand Name *</label>
            <input
              type="text"
              id="brand-name"
              value={brandForm.brand_name}
              onChange={(e) => setBrandForm({...brandForm, brand_name: e.target.value})}
              placeholder="Enter brand name"
              required
              disabled={isSubmitting}
            />
          </div>
          {!organization && (
            <div className="form-group">
              <label htmlFor="brand-org">Parent Organization *</label>
              <CustomSelect
                id="brand-org"
                value={brandForm.organization_id}
                onChange={(e) => setBrandForm({...brandForm, organization_id: e.target.value})}
                required
                disabled={isSubmitting}
              >
                <option value="">Select Organization</option>
                {organizations.map(org => (
                  <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                    {safeString(org.organization_name || org.name)}
                  </option>
                ))}
              </CustomSelect>
              {!brandForm.organization_id && (
                <small>Brands must belong to an organization</small>
              )}
            </div>
          )}
          {organization && (
            <div className="form-group">
              <label>Organization: {safeString(organization.organization_name || organization.name)}</label>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="brand-owner">Brand Owner (Optional)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <CustomSelect
                id="brand-owner"
                value={brandForm.owner_id}
                onChange={(e) => setBrandForm({...brandForm, owner_id: e.target.value})}
                disabled={isSubmitting}
              >
                <option value="" disabled hidden>Select Owner</option>
                {clients.map(client => (
                  <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                    {safeString(client.name)} ({safeString(client.email)})
                  </option>
                ))}
              </CustomSelect>
              {brandForm.owner_id && String(brandForm.owner_id).trim() !== '' && (
                <button
                  type="button"
                  onClick={() => setBrandForm({...brandForm, owner_id: ''})}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: 'var(--color-danger-button)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title="Remove owner"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="form-group">
            <ImageUpload
              value={brandForm.icon_url}
              onChange={(s3_url) => setBrandForm({...brandForm, icon_url: s3_url})}
              onClear={() => setBrandForm({...brandForm, icon_url: ''})}
              label="Brand Icon"
              required={false}
              maxSizeMB={5}
              fileType="brand_icon"
            />
          </div>
          <div className="form-actions">
            <button type="button" className="button-main" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="button-main" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Brand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBrandModal;
